import React, { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Camera,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  LogIn,
  LogOut,
  AlertCircle,
  RefreshCw,
  Navigation,
  Building2,
  Home,
  Briefcase,
  ScanFace,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const MODELS_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model';
const FACE_MATCH_THRESHOLD = 0.5; // Lower = stricter (0.4-0.6 recommended)
const LOCATION_SAMPLE_WINDOW_MS = 8000; // durasi ambil beberapa sampel GPS untuk cari fix paling akurat
const LOCATION_GOOD_ACCURACY_M = 30; // kalau sudah dapat akurasi ini, berhenti lebih awal
const LOCATION_WARN_ACCURACY_M = 150; // di atas ini, tampilkan peringatan akurasi GPS kurang baik
const FACE_DETECT_FAIL_THRESHOLD = 10; // ~3 detik (10 x 300ms) gagal deteksi wajah berturut-turut -> trigger fallback backend

const modeOptions = [
  { value: 'wfo', label: 'Work From Office', icon: Building2, color: 'text-blue-600' },
  { value: 'wfh', label: 'Work From Home', icon: Home, color: 'text-green-600' },
  { value: 'client_visit', label: 'Client Visit', icon: Briefcase, color: 'text-purple-600' },
];

// Calculate Euclidean distance between two face descriptors
const calculateFaceDistance = (descriptor1, descriptor2) => {
  if (!descriptor1 || !descriptor2 || descriptor1.length !== descriptor2.length) return 1;
  let sum = 0;
  for (let i = 0; i < descriptor1.length; i++) {
    sum += Math.pow(descriptor1[i] - descriptor2[i], 2);
  }
  return Math.sqrt(sum);
};

const AttendancePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const faceBackendRef = useRef('webgl'); // backend tfjs yang aktif, dipakai buat auto-fallback ke cpu
  const consecutiveNoFaceRef = useRef(0); // hitung gagal deteksi berturut-turut (indikasi webgl silent-fail)
  
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [mode, setMode] = useState('wfo');
  const [clientAddress, setClientAddress] = useState('');
  const [showClockDialog, setShowClockDialog] = useState(false);
  const [clockType, setClockType] = useState(null);
  const [settings, setSettings] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [faceRegistered, setFaceRegistered] = useState(false);
  const [registeredDescriptor, setRegisteredDescriptor] = useState(null);
  
  // Face detection states
  const [faceapi, setFaceapi] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceModelsLoaded, setFaceModelsLoaded] = useState(false);
  const [verifyingFace, setVerifyingFace] = useState(false);
  const [faceVerified, setFaceVerified] = useState(false);
  const [faceMatchScore, setFaceMatchScore] = useState(null);

  useEffect(() => {
    fetchData();
    loadFaceModels();
    return () => stopCamera();
  }, []);

  const loadFaceModels = async () => {
    try {
      const faceapiModule = await import('@vladmandic/face-api');

      // Force webgl or cpu backend; skip wasm (binary not bundled, causes init failure)
      try {
        await faceapiModule.tf.setBackend('webgl');
        await faceapiModule.tf.ready();
        // Verify webgl actually initialized (can silent-fail on some devices/browsers)
        faceapiModule.tf.tidy(() => faceapiModule.tf.zeros([1]).dataSync());
        faceBackendRef.current = 'webgl';
      } catch (backendError) {
        await faceapiModule.tf.setBackend('cpu');
        await faceapiModule.tf.ready();
        faceBackendRef.current = 'cpu';
      }

      await faceapiModule.nets.tinyFaceDetector.loadFromUri(MODELS_URL);
      await faceapiModule.nets.faceLandmark68Net.loadFromUri(MODELS_URL);
      await faceapiModule.nets.faceRecognitionNet.loadFromUri(MODELS_URL);
      setFaceapi(faceapiModule);
      setFaceModelsLoaded(true);
    } catch (error) {
      console.error('Failed to load face models:', error);
      toast.error('Gagal memuat model deteksi wajah. Cek koneksi internet, lalu refresh halaman.');
    }
  };

  const fetchData = async () => {
    try {
      const [attendanceRes, settingsRes, faceRes, shiftsRes] = await Promise.all([
        api.get('/attendance/today'),
        api.get('/attendance/settings'),
        api.get('/face/check'),
        api.get('/shifts'),
      ]);
      setTodayAttendance(attendanceRes.data);
      setSettings(settingsRes.data);
      setFaceRegistered(faceRes.data.registered);
      setShifts(shiftsRes.data || []);
      
      // Get registered face descriptor for verification
      if (faceRes.data.registered) {
        try {
          const descriptorRes = await api.get('/face/descriptor');
          if (descriptorRes.data.face_descriptor) {
            setRegisteredDescriptor(descriptorRes.data.face_descriptor);
          }
        } catch (e) {
          console.error('Failed to fetch face descriptor:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = useCallback(() => {
    setGettingLocation(true);
    setLocationError(null);
    setLocation(null);

    if (!navigator.geolocation) {
      setLocationError('Geolocation tidak didukung browser');
      setGettingLocation(false);
      return;
    }

    let bestFix = null;
    let watchId = null;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      setGettingLocation(false);
      if (bestFix) {
        setLocation(bestFix);
      } else {
        setLocationError('Gagal mendapatkan lokasi. Pastikan GPS aktif & coba di tempat terbuka.');
      }
    };

    // Ambil beberapa sampel lokasi selama beberapa detik, pilih yang akurasinya paling baik.
    // GPS HP butuh waktu buat "kunci" sinyal satelit; kalau langsung ambil sampel pertama,
    // kadang yang didapat masih dari triangulasi WiFi/seluler yang aksesnya bisa meleset jauh.
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const fix = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        if (!bestFix || fix.accuracy < bestFix.accuracy) {
          bestFix = fix;
        }
        if (fix.accuracy <= LOCATION_GOOD_ACCURACY_M) {
          finish();
        }
      },
      (error) => {
        if (!bestFix) {
          let message = 'Gagal mendapatkan lokasi';
          if (error.code === error.PERMISSION_DENIED) message = 'Izin lokasi ditolak';
          else if (error.code === error.POSITION_UNAVAILABLE) message = 'Lokasi tidak tersedia';
          else if (error.code === error.TIMEOUT) message = 'Timeout lokasi';
          setLocationError(message);
        }
        finish();
      },
      { enableHighAccuracy: true, timeout: LOCATION_SAMPLE_WINDOW_MS, maximumAge: 0 }
    );

    setTimeout(finish, LOCATION_SAMPLE_WINDOW_MS);
  }, []);

  const startCamera = async () => {
    setCameraLoading(true);
    setFaceDetected(false);
    setFaceVerified(false);
    setFaceMatchScore(null);
    consecutiveNoFaceRef.current = 0;
    
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Browser tidak mendukung akses kamera');
      setCameraLoading(false);
      return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 640 } },
        audio: false,
      });
      
      streamRef.current = mediaStream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = async () => {
          try {
            await videoRef.current.play();
            setCameraActive(true);
            setCameraLoading(false);
            startFaceDetection();
          } catch (playError) {
            toast.error('Gagal memutar video');
            setCameraLoading(false);
          }
        };
      }
    } catch (error) {
      console.error('Camera error:', error);
      setCameraLoading(false);
      toast.error(error.name === 'NotAllowedError' ? 'Izin kamera ditolak' : 'Gagal akses kamera');
    }
  };

  const stopCamera = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setFaceDetected(false);
    consecutiveNoFaceRef.current = 0;
  };

  const startFaceDetection = () => {
    if (!faceapi || !videoRef.current) return;

    const registerMiss = async () => {
      setFaceDetected(false);
      consecutiveNoFaceRef.current += 1;

      // Deteksi gagal terus-menerus (baik return kosong ATAU exception) walau kamera aktif ->
      // indikasi WebGL silent-fail (umum di HP Android low-end). Auto-fallback ke backend CPU
      // yang lebih lambat tapi jauh lebih konsisten, tanpa perlu user reload halaman manual.
      if (consecutiveNoFaceRef.current === FACE_DETECT_FAIL_THRESHOLD && faceBackendRef.current === 'webgl') {
        consecutiveNoFaceRef.current = 0;
        toast.info('Deteksi wajah lambat terdeteksi, mencoba metode alternatif...');
        try {
          await faceapi.tf.setBackend('cpu');
          await faceapi.tf.ready();
          faceBackendRef.current = 'cpu';
        } catch (fallbackError) {
          console.error('Gagal fallback ke backend cpu:', fallbackError);
        }
      }
    };

    detectionIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || videoRef.current.paused) return;

      try {
        const detections = await faceapi.detectSingleFace(
          videoRef.current, 
          new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 })
        );

        if (detections) {
          consecutiveNoFaceRef.current = 0;
          setFaceDetected(true);
          return;
        }

        await registerMiss();
      } catch (error) {
        console.error('Face detection error:', error);
        await registerMiss();
      }
    }, 300);
  };

  const captureAndVerifyFace = async () => {
    if (!videoRef.current || !faceapi) return;

    if (!faceDetected) {
      toast.error('Wajah tidak terdeteksi! Posisikan wajah di kamera.');
      return;
    }

    setVerifyingFace(true);

    try {
      // Extract face descriptor from current frame
      const detections = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 }))
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detections) {
        toast.error('Wajah tidak terdeteksi. Coba lagi.');
        setVerifyingFace(false);
        return;
      }

      const currentDescriptor = Array.from(detections.descriptor);

      // Verify against registered face
      if (registeredDescriptor) {
        const distance = calculateFaceDistance(currentDescriptor, registeredDescriptor);
        const matchPercentage = Math.max(0, Math.round((1 - distance) * 100));
        setFaceMatchScore(matchPercentage);

        if (distance > FACE_MATCH_THRESHOLD) {
          toast.error(`Wajah tidak cocok! (${matchPercentage}% kecocokan). Pastikan wajah Anda yang terdaftar.`);
          setFaceVerified(false);
          setVerifyingFace(false);
          return;
        }

        setFaceVerified(true);
        toast.success(`Wajah terverifikasi! (${matchPercentage}% kecocokan)`);
      }

      // Capture photo
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      const photoData = canvas.toDataURL('image/jpeg', 0.7);
      
      setCapturedPhoto(photoData);
      stopCamera();
    } catch (error) {
      console.error('Face verification error:', error);
      toast.error('Gagal verifikasi wajah');
    } finally {
      setVerifyingFace(false);
    }
  };

  const openClockDialog = (type) => {
    if (!faceRegistered) {
      toast.error('Anda harus mendaftarkan wajah terlebih dahulu!');
      return;
    }
    
    setClockType(type);
    setCapturedPhoto(null);
    setLocation(null);
    setLocationError(null);
    setClientAddress('');
    setSelectedShiftId('');
    setFaceDetected(false);
    setFaceVerified(false);
    setFaceMatchScore(null);
    setShowClockDialog(true);
    
    setTimeout(() => getLocation(), 100);
  };

  const closeClockDialog = () => {
    setShowClockDialog(false);
    stopCamera();
    setCapturedPhoto(null);
    setLocation(null);
    setClockType(null);
    setFaceVerified(false);
    setFaceMatchScore(null);
  };

  const handleClock = async () => {
    if (!capturedPhoto) {
      toast.error('Silakan ambil foto terlebih dahulu');
      return;
    }
    if (!location) {
      toast.error('Lokasi belum didapatkan');
      return;
    }
    if (!faceVerified) {
      toast.error('Wajah belum terverifikasi');
      return;
    }
    if (mode === 'client_visit' && !clientAddress.trim()) {
      toast.error('Alamat client wajib diisi');
      return;
    }
    if (clockType === 'in' && shifts.length > 0 && !selectedShiftId) {
      toast.error('Silakan pilih shift terlebih dahulu');
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post('/attendance/clock', {
        tipe: clockType === 'in' ? 'clock_in' : 'clock_out',
        mode: mode,
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        foto_url: capturedPhoto,
        catatan: null,
        alamat_client: mode === 'client_visit' ? clientAddress : null,
        face_match_score: faceMatchScore,
        shift_id: clockType === 'in' ? (selectedShiftId || null) : undefined,
      });

      toast.success(clockType === 'in' ? 'Clock In berhasil!' : 'Clock Out berhasil!');
      setTodayAttendance(response.data);
      closeClockDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal melakukan absensi');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    // Selalu format sebagai tanggal WIB (Asia/Jakarta), bukan ikut timezone browser/UTC.
    // Kalau pakai new Date().toISOString().split('T')[0] lalu di-parse ulang, hasilnya
    // tanggal UTC -> antara jam 00:00-06:59 WIB tanggal yang tampil jadi KEMARIN (mundur 1 hari).
    return date.toLocaleDateString('id-ID', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Jakarta'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user?.employee_id) {
    return (
      <div className="space-y-6" data-testid="attendance-page">
        <h1 className="text-2xl font-bold">Absensi</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Akun belum terhubung dengan data karyawan. Hubungi HR.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const canClockIn = !todayAttendance?.clock_in;
  const canClockOut = todayAttendance?.clock_in && !todayAttendance?.clock_out;

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <h1 className="text-2xl font-bold">Absensi</h1>
        <p className="text-muted-foreground text-sm mt-1" data-testid="attendance-header-date">{formatDate(new Date())}</p>
      </div>

      {/* Face Registration Warning */}
      {!faceRegistered && (
        <Alert variant="destructive" className="border-red-500 bg-red-50">
          <ScanFace className="h-5 w-5" />
          <AlertDescription className="ml-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="font-medium">Anda WAJIB mendaftarkan wajah sebelum bisa absen!</span>
              <Button size="sm" variant="destructive" onClick={() => navigate('/face-registration')}>
                Daftar Wajah
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Today's Status */}
      <Card className="border-2 border-primary/20">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />Status Hari Ini
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!todayAttendance ? (
            <div className="text-center py-6">
              <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-muted-foreground">Belum ada absensi hari ini</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <LogIn className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Clock In</p>
                    <p className="font-semibold">{formatTime(todayAttendance.clock_in)}</p>
                  </div>
                </div>
                <Badge className={todayAttendance.status === 'hadir' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                  {todayAttendance.status}
                </Badge>
              </div>

              {todayAttendance.shift_nama && (
                <div className="flex items-center gap-2 px-1 text-sm text-muted-foreground" data-testid="today-shift-info">
                  <Clock className="w-3.5 h-3.5" />Shift: <span className="font-medium text-foreground">{todayAttendance.shift_nama}</span>
                </div>
              )}

              {todayAttendance.clock_out && (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <LogOut className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Clock Out</p>
                      <p className="font-semibold">{formatTime(todayAttendance.clock_out)}</p>
                    </div>
                  </div>
                  {todayAttendance.total_jam && (
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Total</p>
                      <p className="font-semibold">{todayAttendance.total_jam.toFixed(1)} jam</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4">
        <Button size="lg" className="h-16 text-base gap-2" onClick={() => openClockDialog('in')} disabled={!canClockIn || !faceRegistered}>
          <LogIn className="w-5 h-5" />Clock In
        </Button>
        <Button size="lg" variant={canClockOut ? "default" : "secondary"} className="h-16 text-base gap-2" onClick={() => openClockDialog('out')} disabled={!canClockOut || !faceRegistered}>
          <LogOut className="w-5 h-5" />Clock Out
        </Button>
      </div>

      {!faceRegistered && (
        <p className="text-center text-sm text-red-500">Tombol absensi nonaktif karena wajah belum terdaftar</p>
      )}

      {/* Work Hours Info */}
      {settings && (
        <Card>
          <CardContent className="p-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Jam Kerja</span><span>{settings.work_hours.start} - {settings.work_hours.end}</span></div>
            <div className="flex justify-between mt-1"><span className="text-muted-foreground">Toleransi</span><span>{settings.work_hours.late_tolerance_minutes} menit</span></div>
          </CardContent>
        </Card>
      )}

      {/* Clock Dialog */}
      <Dialog open={showClockDialog} onOpenChange={setShowClockDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{clockType === 'in' ? 'Clock In' : 'Clock Out'}</DialogTitle>
            <DialogDescription>Verifikasi wajah untuk absensi</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Mode Selection */}
            <div className="space-y-2">
              <Label>Mode Kerja</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {modeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon className={`w-4 h-4 ${opt.color}`} />{opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {mode === 'client_visit' && (
              <div className="space-y-2">
                <Label>Alamat Client *</Label>
                <Input value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} placeholder="Masukkan alamat" />
              </div>
            )}

            {/* Shift Selection - hanya saat Clock In, clock out otomatis pakai shift yang sama */}
            {clockType === 'in' && shifts.length > 0 && (
              <div className="space-y-2">
                <Label>Shift *</Label>
                <Select value={selectedShiftId} onValueChange={setSelectedShiftId}>
                  <SelectTrigger data-testid="select-shift-trigger"><SelectValue placeholder="Pilih shift kerja hari ini" /></SelectTrigger>
                  <SelectContent>
                    {shifts.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`shift-option-${s.id}`}>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.warna }} />
                          {s.nama} ({s.jam_masuk} - {s.jam_keluar})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Portrait Camera */}
            <div className="space-y-2">
              <Label>Foto & Verifikasi Wajah</Label>
              <div className="relative w-full max-w-[240px] mx-auto" style={{ aspectRatio: '3/4' }}>
                <div className="absolute inset-0 bg-slate-900 rounded-lg overflow-hidden">
                  {!cameraActive && !capturedPhoto && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      <Camera className="w-10 h-10 mb-3 opacity-50" />
                      <Button size="sm" variant="secondary" onClick={startCamera} disabled={cameraLoading || !faceModelsLoaded}>
                        {cameraLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Membuka...</> : 'Buka Kamera'}
                      </Button>
                    </div>
                  )}

                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ display: cameraActive && !capturedPhoto ? 'block' : 'none' }}
                    className="w-full h-full object-cover"
                  />
                  
                  {cameraActive && !capturedPhoto && (
                    <>
                      <div className="absolute top-2 left-2">
                        {faceDetected ? (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-full text-xs">
                            <CheckCircle className="w-3 h-3" /> Wajah OK
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded-full text-xs animate-pulse">
                            <XCircle className="w-3 h-3" /> Tidak Ada Wajah
                          </div>
                        )}
                      </div>
                      
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                        <Button size="sm" onClick={captureAndVerifyFace} disabled={!faceDetected || verifyingFace}>
                          {verifyingFace ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Verifikasi...</> : <><Camera className="w-4 h-4 mr-1" />Ambil & Verifikasi</>}
                        </Button>
                      </div>
                    </>
                  )}

                  {capturedPhoto && (
                    <>
                      <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" />
                      <div className="absolute top-2 left-2">
                        {faceVerified ? (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-full text-xs">
                            <ShieldCheck className="w-3 h-3" /> Terverifikasi {faceMatchScore}%
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded-full text-xs">
                            <ShieldAlert className="w-3 h-3" /> Tidak Cocok
                          </div>
                        )}
                      </div>
                      <div className="absolute top-2 right-2">
                        <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => { setCapturedPhoto(null); setFaceVerified(false); startCamera(); }}>
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {capturedPhoto && !faceVerified && (
                <p className="text-xs text-red-500 text-center">Wajah tidak cocok dengan data terdaftar. Ambil ulang foto.</p>
              )}
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label>Lokasi</Label>
              <div className="p-2 bg-slate-50 rounded-lg text-sm">
                {gettingLocation ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />Mencari lokasi akurat...
                  </div>
                ) : locationError ? (
                  <div className="flex items-center justify-between">
                    <span className="text-red-500 text-xs">{locationError}</span>
                    <Button size="sm" variant="ghost" onClick={getLocation}><RefreshCw className="w-3 h-3" /></Button>
                  </div>
                ) : location ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs" data-testid="location-coords">
                          {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)} (akurasi ±{Math.round(location.accuracy)}m)
                        </span>
                      </div>
                      <Button size="sm" variant="ghost" onClick={getLocation} data-testid="retry-location-btn"><RefreshCw className="w-3 h-3" /></Button>
                    </div>
                    {location.accuracy > LOCATION_WARN_ACCURACY_M && (
                      <div className="flex items-center gap-1.5 text-amber-600 text-xs" data-testid="location-accuracy-warning">
                        <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                        Akurasi GPS kurang baik. Coba pindah ke tempat terbuka lalu tekan tombol ulang di samping.
                      </div>
                    )}
                  </div>
                ) : (
                  <Button size="sm" variant="ghost" onClick={getLocation} className="w-full">
                    <Navigation className="w-4 h-4 mr-1" />Dapatkan Lokasi
                  </Button>
                )}
              </div>
            </div>

            <Button className="w-full" onClick={handleClock} disabled={submitting || !capturedPhoto || !location || !faceVerified || (clockType === 'in' && shifts.length > 0 && !selectedShiftId)} data-testid="submit-clock-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Memproses...</> : (
                <>{clockType === 'in' ? <LogIn className="w-4 h-4 mr-2" /> : <LogOut className="w-4 h-4 mr-2" />}
                {clockType === 'in' ? 'Clock In' : 'Clock Out'}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default AttendancePage;
